/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: tabs\VoicemailTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Extensions module.
----------------------------------------------------------------*/

import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Toggle } from '../../../components/ui/Toggle';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { TimezoneSelect } from '../../../components/ui/TimezoneSelect';
import SecretField from '../../../components/ui/SecretField';
import { LanguageSelector } from '../components/LanguageSelector';
import { FormBox } from '../../../components/ui/FormBox';
import SelectFaxDevice from '../../../components/ui/SelectFaxDevice';
import { useI18n } from '../../../hooks/useI18n';
import { useDebugMode } from '../../../hooks/useDebugMode';
import { Voicemail, Printer } from 'lucide-react';

import type { ExtensionFormState, VoicemailSettingsState } from '../types';

const generateVoicemailPin = () => Math.floor(1000 + Math.random() * 9000).toString();

interface VoicemailTabProps {
  formData: ExtensionFormState;
  voicemailData: VoicemailSettingsState;
  errors: Record<string, string>;
  onChange: <K extends keyof ExtensionFormState>(field: K, value: ExtensionFormState[K]) => void;
  onVoicemailChange: <K extends keyof VoicemailSettingsState>(field: K, value: VoicemailSettingsState[K]) => void;
  faxDevices?: Array<{ id: number; name: string; number: string }>;
}

export function VoicemailTab({
  formData,
  voicemailData,
  errors,
  onChange,
  onVoicemailChange,
  faxDevices = [],
}: VoicemailTabProps) {
  const { t } = useI18n();
  const { isDebugModeActive } = useDebugMode();

  return (
    <div className="space-y-4">
      {/* -- Voicemail -- */}
      <FormBox
        title={t('extensions.voicemail.title')}
        icon={<Voicemail className="h-5 w-5" />}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-start">
          <SimpleTooltip content={t('extensions.fields.voicemailEnabled.tooltip')} title={t('extensions.fields.voicemailEnabled.label')} position="top" maxWidth="300px">
            <Label htmlFor="voicemailEnabled" className="text-left">{t('extensions.fields.voicemailEnabled.label')}</Label>
          </SimpleTooltip>
          <Toggle id="voicemailEnabled" value={formData.voicemailEnabled} onChange={(v) => onChange('voicemailEnabled', v)} />
          <div className="col-span-2" />

          {formData.voicemailEnabled && (
            <>
              <SimpleTooltip content={isDebugModeActive ? `${t('extensions.voicemail.password.tooltip')}\n\n${t('extensions.debug.field')}: ${t('extensions.debug.vm_password')}` : t('extensions.voicemail.password.tooltip')} title={t('extensions.voicemail.password.label')} position="top" maxWidth="300px">
                <Label htmlFor="vmPassword" required className="text-left">{t('extensions.voicemail.password.label')}</Label>
              </SimpleTooltip>
              <SecretField id="vmPassword" type="input" value={voicemailData.password} onChange={(v) => onVoicemailChange('password', v)} placeholder={t('extensions.voicemail.password.placeholder')} error={errors.vmPassword} showGenerate onGenerate={() => onVoicemailChange('password', generateVoicemailPin())} showToggle blurClass="blur-sm" />

              <SimpleTooltip content={t('extensions.voicemail.maxMessages.tooltip')} title={t('extensions.voicemail.maxMessages.label')} position="top" maxWidth="300px">
                <Label htmlFor="maxMessages" className="text-left">{t('extensions.voicemail.maxMessages.label')}</Label>
              </SimpleTooltip>
              <Input id="maxMessages" type="number" value={voicemailData.maxMessages} onChange={(e) => onVoicemailChange('maxMessages', parseInt(e.target.value))} placeholder={t('extensions.voicemail.maxMessages.placeholder')} />

              <SimpleTooltip content={isDebugModeActive ? `${t('extensions.voicemail.email.tooltip')}\n\n${t('extensions.debug.field')}: ${t('extensions.debug.vm_email')}` : t('extensions.voicemail.email.tooltip')} title={t('extensions.voicemail.email.label')} position="top" maxWidth="300px">
                <Label htmlFor="vmEmail" className="text-left">{t('extensions.voicemail.email.label')}</Label>
              </SimpleTooltip>
              <Input id="vmEmail" type="email" value={voicemailData.email} onChange={(e) => onVoicemailChange('email', e.target.value)} placeholder={t('extensions.voicemail.email.placeholder')} error={errors.vmEmail} />

              <SimpleTooltip content={isDebugModeActive ? `${t('extensions.voicemail.language.tooltip')}\n\n${t('extensions.debug.field')}: ${t('extensions.debug.vm_language')}` : t('extensions.voicemail.language.tooltip')} title={t('extensions.voicemail.language.label')} position="top" maxWidth="300px">
                <Label htmlFor="vmLanguage" className="text-left">{t('extensions.voicemail.language.label')}</Label>
              </SimpleTooltip>
              <LanguageSelector value={voicemailData.language} onChange={(v) => onVoicemailChange('language', v)} showVoiceSelector={false} disabled={false} error={errors.vmLanguage} showLabel={false} />

              <SimpleTooltip content={t('extensions.voicemail.timezone.tooltip')} title={t('extensions.voicemail.timezone.label')} position="top" maxWidth="300px">
                <Label htmlFor="timezone" className="text-left">{t('extensions.voicemail.timezone.label')}</Label>
              </SimpleTooltip>
              <TimezoneSelect value={voicemailData.timezone || 'America/Managua'} onChange={(v) => onVoicemailChange('timezone', v)} placeholder={t('extensions.voicemail.timezone.placeholder')} />

              <SimpleTooltip content={t('extensions.voicemail.notificationEnabled.tooltip')} title={t('extensions.voicemail.notificationEnabled.label')} position="top" maxWidth="300px">
                <Label htmlFor="notificationEnabled" className="text-left">{t('extensions.voicemail.notificationEnabled.label')}</Label>
              </SimpleTooltip>
              <Toggle id="notificationEnabled" value={voicemailData.notificationEnabled} onChange={(v) => onVoicemailChange('notificationEnabled', v)} />

              <SimpleTooltip content={isDebugModeActive ? `${t('extensions.voicemail.emailAttachment.tooltip')}\n\n${t('extensions.debug.field')}: ${t('extensions.debug.vm_attach_file')}` : t('extensions.voicemail.emailAttachment.tooltip')} title={t('extensions.voicemail.emailAttachment.label')} position="top" maxWidth="300px">
                <Label htmlFor="emailAttachment" className="text-left">{t('extensions.voicemail.emailAttachment.label')}</Label>
              </SimpleTooltip>
              <Toggle id="emailAttachment" value={voicemailData.emailAttachment} onChange={(v) => onVoicemailChange('emailAttachment', v)} />

              <SimpleTooltip content={isDebugModeActive ? `${t('extensions.voicemail.transcribeEnabled.tooltip')}\n\n${t('extensions.debug.field')}: ${t('extensions.debug.vm_transcribe')}` : t('extensions.voicemail.transcribeEnabled.tooltip')} title={t('extensions.voicemail.transcribeEnabled.label')} position="top" maxWidth="300px">
                <Label htmlFor="transcribeEnabled" className="text-left">{t('extensions.voicemail.transcribeEnabled.label')}</Label>
              </SimpleTooltip>
              <Toggle id="transcribeEnabled" value={voicemailData.transcribeEnabled} onChange={(v) => onVoicemailChange('transcribeEnabled', v)} />

              <SimpleTooltip content={t('extensions.voicemail.greeting.tooltip')} title={t('extensions.voicemail.greeting.label')} position="top" maxWidth="300px">
                <Label htmlFor="greeting" className="text-left">{t('extensions.voicemail.greeting.label')}</Label>
              </SimpleTooltip>
              <Input id="greeting" value={voicemailData.greeting} onChange={(e) => onVoicemailChange('greeting', e.target.value)} placeholder={t('extensions.voicemail.greeting.placeholder')} />

              <SimpleTooltip content={t('extensions.voicemail.storagePath.tooltip')} title={t('extensions.voicemail.storagePath.label')} position="top" maxWidth="300px">
                <Label htmlFor="storagePath" className="text-left">{t('extensions.voicemail.storagePath.label')}</Label>
              </SimpleTooltip>
              <Input id="storagePath" value={voicemailData.storagePath} onChange={(e) => onVoicemailChange('storagePath', e.target.value)} placeholder={t('extensions.voicemail.storagePath.placeholder')} />

              <SimpleTooltip content={t('extensions.voicemail.emailTemplate.tooltip')} title={t('extensions.voicemail.emailTemplate.label')} position="top" maxWidth="300px">
                <Label htmlFor="emailTemplate" className="text-left">{t('extensions.voicemail.emailTemplate.label')}</Label>
              </SimpleTooltip>
              <Input id="emailTemplate" value={voicemailData.emailTemplate} onChange={(e) => onVoicemailChange('emailTemplate', e.target.value)} placeholder={t('extensions.voicemail.emailTemplate.placeholder')} />
              <div className="col-span-2" />
            </>
          )}
        </div>
      </FormBox>

      {/* -- Fax (inline � always shown, fields from sip_extensions) -- */}
      <FormBox
        title={t('extensions.fax.title', 'Fax')}
        icon={<Printer className="h-5 w-5" />}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-start">
          {/* Fax Enabled */}
          <SimpleTooltip content={t('extensions.fax.enabled.tooltip', 'Enable fax reception for this extension.')} title={t('extensions.fax.enabled.label', 'Fax Enabled')} position="top" maxWidth="300px">
            <Label htmlFor="faxEnabled" className="text-left">{t('extensions.fax.enabled.label', 'Fax Enabled')}</Label>
          </SimpleTooltip>
          <Toggle id="faxEnabled" value={formData.faxEnabled} onChange={(v) => onChange('faxEnabled', v)} />
          <div className="col-span-2" />

          {formData.faxEnabled && (
            <>
              {/* Row 1: Fax Device + Fax Number */}
              <SimpleTooltip content={t('extensions.fax.device.tooltip', 'Fax device used for sending and receiving faxes on this extension.')} title={t('extensions.fax.device.label', 'Fax Device')} position="top" maxWidth="300px">
                <Label htmlFor="faxDeviceId" className="text-left">{t('extensions.fax.device.label', 'Fax Device')}</Label>
              </SimpleTooltip>
              <SelectFaxDevice
                value={formData.faxDeviceId ?? null}
                onChange={(v) => onChange('faxDeviceId', v === null ? null : Number(v))}
                faxDevices={faxDevices}
                placeholder={t('extensions.fax.device.placeholder', '\u2014 Select device \u2014')}
              />

              <SimpleTooltip content={t('extensions.fax.number.tooltip', 'DID or fax number assigned to this extension.')} title={t('extensions.fax.number.label', 'Fax Number')} position="top" maxWidth="300px">
                <Label htmlFor="faxNumber" className="text-left">{t('extensions.fax.number.label', 'Fax Number')}</Label>
              </SimpleTooltip>
              <Input id="faxNumber" value={formData.faxNumber || ''} onChange={(e) => onChange('faxNumber', e.target.value)} placeholder={t('extensions.fax.number.placeholder', 'e.g. +15551234567')} />

              {/* Row 2: Destination Email + From Email */}
              <SimpleTooltip content={t('extensions.fax.destinationEmail.tooltip', 'Email address where received faxes (PDF) will be delivered.')} title={t('extensions.fax.destinationEmail.label', 'Destination Email')} position="top" maxWidth="300px">
                <Label htmlFor="faxEmailTo" className="text-left">{t('extensions.fax.destinationEmail.label', 'Destination Email')}</Label>
              </SimpleTooltip>
              <Input id="faxEmailTo" type="email" value={formData.faxEmailTo || ''} onChange={(e) => onChange('faxEmailTo', e.target.value)} placeholder={t('extensions.fax.destinationEmail.placeholder', 'user@company.com')} />

              <SimpleTooltip content={t('extensions.fax.fromEmail.tooltip', 'Sender address used in fax email notifications.')} title={t('extensions.fax.fromEmail.label', 'From Email')} position="top" maxWidth="300px">
                <Label htmlFor="faxEmailFrom" className="text-left">{t('extensions.fax.fromEmail.label', 'From Email')}</Label>
              </SimpleTooltip>
              <Input id="faxEmailFrom" type="email" value={formData.faxEmailFrom || ''} onChange={(e) => onChange('faxEmailFrom', e.target.value)} placeholder={t('extensions.fax.fromEmail.placeholder', 'fax@company.com')} />
            </>
          )}
        </div>
      </FormBox>
    </div>
  );
}
